import { getCartLines, getMaticCount } from "@/cart/displayCart";
import type { ReferralReward } from "@/cart/referralCode";
import {
  MATIC_BAG_PASS_VARIANT_ID,
  MATIC_REFERRAL_BAG_PASS_VARIANT_ID,
} from "@/consts/shopify";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import {
  isLegacyReferralReward,
  isReferralRewardBagPass,
  isStandardBagPass,
} from "@/utils/ShopifyHelpers";

export const removeReferralRewardLines = async (cart: Cart) => {
  const lineIds = cart.lines.edges
    .filter(
      ({ node }) =>
        isReferralRewardBagPass(node) || isLegacyReferralReward(node),
    )
    .map(({ node }) => node.id);

  if (lineIds.length === 0) {
    return cart;
  }

  const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
    cartId: cart.id,
    lineIds,
  });

  return cartLinesRemove?.cart as Cart | undefined;
};

const syncReferralEditionLines = async (cart: Cart, quantity: number) => {
  const rewardLines = cart.lines.edges
    .filter(({ node }) => isReferralRewardBagPass(node))
    .map(({ node }) => node);

  const [primaryLine, ...duplicateLines] = rewardLines;
  const lineIdsToRemove = [
    ...duplicateLines,
    ...(quantity === 0 && primaryLine ? [primaryLine] : []),
  ].map((line) => line.id);

  let nextCart: Cart = cart;

  if (lineIdsToRemove.length > 0) {
    const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
      cartId: cart.id,
      lineIds: lineIdsToRemove,
    });
    const cartAfterRemoval = cartLinesRemove?.cart as Cart | undefined;

    if (!cartAfterRemoval) {
      throw new Error(
        "syncReferralRewardBagPass: Failed to remove stale reward lines",
      );
    }

    nextCart = cartAfterRemoval;
  }

  if (quantity === 0) {
    return nextCart;
  }

  if (!primaryLine) {
    const { cartLinesAdd } = await GRAPHQL_CLIENT.AddToCart({
      cartId: nextCart.id,
      lines: [
        {
          merchandiseId: MATIC_REFERRAL_BAG_PASS_VARIANT_ID,
          quantity,
          attributes: [],
        },
      ],
    });
    const addedCart = cartLinesAdd?.cart as Cart | undefined;

    if (!addedCart) {
      throw new Error(
        "syncReferralRewardBagPass: Failed to add referral reward line",
      );
    }

    return addedCart;
  }

  if (primaryLine.quantity !== quantity) {
    const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
      cartId: nextCart.id,
      lines: [{ id: primaryLine.id, quantity }],
    });
    const updatedCart = cartLinesUpdate?.cart as Cart | undefined;

    if (!updatedCart) {
      throw new Error(
        "syncReferralRewardBagPass: Failed to update referral reward quantity",
      );
    }

    return updatedCart;
  }

  return nextCart;
};

// Tops the standard pass up to one per Matic but never lowers it: the reward
// shares its product with passes the shopper chose themselves.
const raiseStandardBagPassToMatics = async (cart: Cart) => {
  const maticCount = getMaticCount(cart);
  const passLines = getCartLines(cart).filter(isStandardBagPass);
  const passCount = passLines.reduce((total, line) => total + line.quantity, 0);
  const [firstPassLine] = passLines;

  if (passCount >= maticCount) {
    return cart;
  }

  if (!firstPassLine) {
    const { cartLinesAdd } = await GRAPHQL_CLIENT.AddToCart({
      cartId: cart.id,
      lines: [
        {
          merchandiseId: MATIC_BAG_PASS_VARIANT_ID,
          quantity: maticCount,
          attributes: [],
        },
      ],
    });
    const addedCart = cartLinesAdd?.cart as Cart | undefined;

    if (!addedCart) {
      throw new Error(
        "syncReferralRewardBagPass: Failed to add standard bag pass reward",
      );
    }

    return addedCart;
  }

  const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
    cartId: cart.id,
    lines: [
      {
        id: firstPassLine.id,
        quantity: firstPassLine.quantity + maticCount - passCount,
      },
    ],
  });
  const updatedCart = cartLinesUpdate?.cart as Cart | undefined;

  if (!updatedCart) {
    throw new Error(
      "syncReferralRewardBagPass: Failed to raise standard bag pass reward",
    );
  }

  return updatedCart;
};

/**
 * Gives the cart one of the code's free bag passes per Matic. A referral-edition
 * line is only ever right for a code that gives that edition; under any other
 * code it would be charged at full price, so it is removed.
 */
export const syncReferralRewardBagPass = async (
  cart: Cart,
  reward: ReferralReward,
) => {
  const referralEditionQuantity =
    reward === "referral-bag-pass" ? getMaticCount(cart) : 0;
  const nextCart = await syncReferralEditionLines(
    cart,
    referralEditionQuantity,
  );

  return reward === "bag-pass"
    ? raiseStandardBagPassToMatics(nextCart)
    : nextCart;
};
